import { useMemo, useState } from "react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { cn } from "~/lib/utils";

// Wire shape: the generate-preview Lambda emits pandas split-orient JSON
// (`df.to_json(orient="split")`). `index` is included by pandas but unused
// in the view — we render the original row order.
export interface DataframeSplit {
  columns: string[];
  index: ReadonlyArray<string | number>;
  data: unknown[][];
}

interface DataframeTableProps {
  split: DataframeSplit;
  pageSize?: number;
  // Total rows in the source dataframe before server-side truncation.
  // When larger than the rows in `split`, the footer flags the preview as
  // partial so users know to download for the full dataset.
  totalRows: number;
  displayedRows: number;
  // Whether `totalRows` is the source's exact count. When false (e.g. a draft
  // view, whose row count is unknown), the footer presents the rows as a sample
  // instead of claiming `totalRows` is the true total.
  totalKnown?: boolean;
}

const DEFAULT_PAGE_SIZE = 10;

type Row = Record<string, unknown>;

const renderCell = (value: unknown) => {
  if (value === null || value === undefined) {
    return <span className="italic text-muted-foreground">null</span>;
  }
  if (typeof value === "number") {
    return Number.isInteger(value) ? value : value.toFixed(4);
  }
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

// A column reads as numeric when every non-null value in the preview is a
// number. Numeric columns are right-aligned with tabular figures so their
// digits line up down the column and are easy to scan and compare.
const isNumericColumn = (data: unknown[][], colIndex: number): boolean => {
  let sawValue = false;
  for (const row of data) {
    const value = row[colIndex];
    if (value === null || value === undefined) continue;
    if (typeof value !== "number") return false;
    sawValue = true;
  }
  return sawValue;
};

export const DataframeTable = ({
  split,
  pageSize = DEFAULT_PAGE_SIZE,
  totalRows,
  displayedRows,
  totalKnown = true,
}: DataframeTableProps) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState("");

  const rows = useMemo<Row[]>(
    () =>
      split.data.map((row) => {
        const obj: Row = {};
        split.columns.forEach((col, i) => {
          obj[col] = row[i];
        });
        return obj;
      }),
    [split]
  );

  // The ids of columns whose values are all numeric, so the header and cells
  // can right-align them. Keyed by the same id the column defs use below.
  const numericColumnIds = useMemo(() => {
    const ids = new Set<string>();
    split.columns.forEach((name, i) => {
      if (isNumericColumn(split.data, i)) ids.add(name || `__col_${i}__`);
    });
    return ids;
  }, [split]);

  // accessorFn + explicit `id` (rather than accessorKey) avoids two bugs:
  // 1) accessorKey: "" crashes for unnamed index columns
  // 2) accessorKey treats dots as nested-path separators, breaking names like "a.b"
  const columns = useMemo<ColumnDef<Row>[]>(
    () =>
      split.columns.map((name, i) => {
        const id = name || `__col_${i}__`;
        const numeric = numericColumnIds.has(id);
        return {
          id,
          accessorFn: (row) => row[name],
          header: ({ column }) => {
            const sorted = column.getIsSorted();
            return (
              <button
                type="button"
                onClick={() => column.toggleSorting(sorted === "asc")}
                aria-label={`Sort by ${name}`}
                className={cn(
                  "flex select-none items-center gap-1 transition-colors hover:text-foreground",
                  numeric && "w-full justify-end",
                  sorted ? "text-foreground" : "text-muted-foreground"
                )}
              >
                {name}
                {sorted === "asc" ? (
                  <ArrowUp className="size-3" />
                ) : sorted === "desc" ? (
                  <ArrowDown className="size-3" />
                ) : (
                  <ArrowUpDown className="size-3 opacity-50" />
                )}
              </button>
            );
          },
          cell: ({ getValue }) => renderCell(getValue()),
        };
      }),
    [split.columns, numericColumnIds]
  );

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize } },
  });

  const filteredCount = table.getFilteredRowModel().rows.length;
  const pageIndex = table.getState().pagination.pageIndex;
  const totalPages = table.getPageCount();
  const startRow = pageIndex * pageSize + 1;
  const endRow = Math.min((pageIndex + 1) * pageSize, filteredCount);
  const serverTruncated = totalKnown && totalRows > displayedRows;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Input
          placeholder="Filter all columns..."
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          aria-label="Filter table"
          className="h-8 w-64 text-xs focus-visible:ring-0"
        />
        {globalFilter && (
          <span className="text-xs text-muted-foreground">
            {filteredCount} result{filteredCount === 1 ? "" : "s"}
          </span>
        )}
      </div>

      <Table tableContainerProps={{ className: "rounded-md border" }}>
        <TableHeader>
          {table.getHeaderGroups().map((group) => (
            <TableRow key={group.id} className="bg-muted/50">
              {group.headers.map((header) => (
                <TableHead
                  key={header.id}
                  className={cn("text-xs", numericColumnIds.has(header.column.id) && "text-right")}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="h-16 text-center text-muted-foreground"
              >
                No results.
              </TableCell>
            </TableRow>
          ) : (
            table.getRowModel().rows.map((row, rowIndex) => (
              <TableRow key={row.id} className={cn(rowIndex % 2 === 1 && "bg-muted/20")}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    className={cn(
                      "text-sm",
                      numericColumnIds.has(cell.column.id) && "text-right tabular-nums"
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {filteredCount === 0 ? (
            "0 rows"
          ) : !totalKnown ? (
            <>
              {startRow}&ndash;{endRow} of {displayedRows} (sample)
            </>
          ) : serverTruncated ? (
            <>
              {startRow}&ndash;{endRow} of {displayedRows} (from {totalRows.toLocaleString()} total)
            </>
          ) : (
            <>
              {startRow}&ndash;{endRow} of {filteredCount}
            </>
          )}
        </span>
        {totalPages > 1 && (
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              aria-label="Previous page"
              className="h-7 has-[>svg]:px-1.5"
            >
              <ChevronLeft className="size-4" />
            </Button>
            <span className="px-2">
              Page {pageIndex + 1} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              aria-label="Next page"
              className="h-7 has-[>svg]:px-1.5"
            >
              <ChevronRight className="size-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
